import React, { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLazyQuery } from "@apollo/client";
import { useOnClickOutside } from "usehooks-ts";

import Button from "components/Button";
import Modal from "components/Modal";
import useDebounce from "hooks/useDebounce";
import { FUNDING_REQUEST_CONTRACT_TYPES_MAP } from "helpers/constantTypes";
import type { IPaymentProfile } from "models/HaulPay";
import { selectUserDetails } from "store/user/selector";
import { getPaymentProfileRelationAPI } from "services/api/haulpay";
import { SEARCH_PAYMENT_PROFILE } from "features/main/HaulPay/Shared/CarrierSelector";
import type { NonFactoredBillOutToPayCarrierResult } from "features/main/HaulPay/Shared/nonFactoredBillOutToPayCarrierFlow";
import type { PreviousMetadata } from "features/main/HaulPay/Shared/nonFactoredToFactoredFlow";

const mapGqlToPaymentProfile = (gqlProfile: any): Partial<IPaymentProfile> => ({
  id: gqlProfile.id,
  company_name: gqlProfile.companyName,
  company_mc: gqlProfile.companyMc,
  company_dot: gqlProfile.companyDot,
  owner_email: gqlProfile.ownerEmail,
  status: gqlProfile.status?.toLowerCase(),
  factoring_company: gqlProfile.factoringCompany,
  is_connected: gqlProfile.isConnected,
  payout_days: gqlProfile.payoutDays,
  factoring_fee_split_rate: gqlProfile.factoringFeeSplitRate,
  accounts: gqlProfile.accounts
});

export default function NonFactoredBillOutToPayCarrierModal({
  isOpen,
  closeModal,
  onConfirm,
  onCloseModal,
  fromContractType,
  toContractType,
  previousMetadata
}: Readonly<{
  isOpen: boolean;
  closeModal: () => void;
  onConfirm?: (result: NonFactoredBillOutToPayCarrierResult) => void;
  onCloseModal?: () => void;
  fromContractType?: string;
  toContractType?: string;
  previousMetadata?: PreviousMetadata;
}>) {
  const userDetails = useSelector(selectUserDetails);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const [carrierSearch, setCarrierSearch] = useState("");
  const [isDropdownVisible, setIsDropdownVisible] = useState(false);
  const [selectedCarrier, setSelectedCarrier] = useState<Partial<IPaymentProfile> | null>(null);
  const [carrierInvoiceAmount, setCarrierInvoiceAmount] = useState("");

  const debouncedSearch = useDebounce(carrierSearch, 500);

  const [fetchProfiles, { data: profileData, loading: isLoadingCarriers }] =
    useLazyQuery(SEARCH_PAYMENT_PROFILE);

  useOnClickOutside([dropdownRef], () => setIsDropdownVisible(false));

  const hasPreviousMetadata = Boolean(previousMetadata);
  const [isLoadingPreviousCarrier, setIsLoadingPreviousCarrier] = useState(false);

  useEffect(() => {
    if (!previousMetadata || !userDetails?.factoring_id) return;

    const amount = previousMetadata.previousPaymentProfileAmount;
    if (amount != null) {
      setCarrierInvoiceAmount(String(amount / 100));
    }

    let cancelled = false;
    setIsLoadingPreviousCarrier(true);
    getPaymentProfileRelationAPI(
      userDetails.factoring_id,
      previousMetadata.previousPaymentProfileId
    )
      .then(({ data: relation }) => {
        if (cancelled) return;
        const pp = relation.payment_profile ?? {};
        const profile: Partial<IPaymentProfile> = {
          id: pp.id,
          company_name: pp.company_name,
          company_mc: pp.company_mc,
          company_dot: pp.company_dot,
          owner_email: pp.owner_email,
          status: pp.status,
          factoring_company: pp.factoring_company,
          payout_days: relation.payout_days,
          factoring_fee_split_rate: relation.factoring_fee_split_rate,
          accounts: pp.accounts
        };
        setSelectedCarrier(profile);
        setCarrierSearch(profile.company_name ?? "");
      })
      .catch(() => {})
      .finally(() => {
        if (!cancelled) setIsLoadingPreviousCarrier(false);
      });

    return () => {
      cancelled = true;
    };
  }, [previousMetadata, userDetails?.factoring_id]);

  useEffect(() => {
    if (debouncedSearch.trim()) {
      fetchProfiles({
        variables: {
          factoringId: userDetails?.factoring_id,
          status: "approved,needs_review,pending,declined",
          carrierDba: debouncedSearch,
          first: 10,
          myCarriers: true
        }
      });
    }
  }, [debouncedSearch, userDetails?.factoring_id, fetchProfiles]);

  const profileList: Partial<IPaymentProfile>[] =
    profileData?.searchPaymentProfile?.data?.map(mapGqlToPaymentProfile) ?? [];

  const fromLabel =
    FUNDING_REQUEST_CONTRACT_TYPES_MAP[
      fromContractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
    ] ?? fromContractType;
  const toLabel =
    FUNDING_REQUEST_CONTRACT_TYPES_MAP[
      toContractType as keyof typeof FUNDING_REQUEST_CONTRACT_TYPES_MAP
    ] ?? toContractType;

  const invoiceAmountValue = parseFloat(carrierInvoiceAmount);
  const isConfirmDisabled =
    !selectedCarrier ||
    !carrierInvoiceAmount ||
    Number.isNaN(invoiceAmountValue) ||
    invoiceAmountValue <= 0;

  const handleCarrierSelect = (profile: Partial<IPaymentProfile>) => {
    setSelectedCarrier(profile);
    setCarrierSearch(profile.company_name ?? "");
    setIsDropdownVisible(false);
  };

  const handleCancel = () => {
    onCloseModal?.();
    closeModal();
  };

  const handleConfirm = () => {
    if (!selectedCarrier) return;
    const amount = parseFloat(carrierInvoiceAmount);
    if (Number.isNaN(amount) || amount <= 0) return;

    onConfirm?.({
      carrier: selectedCarrier,
      carrierInvoiceAmount: amount
    });
    closeModal();
  };

  return (
    <Modal
      isOpen={isOpen}
      closeModal={handleCancel}
      size="sm"
      wrapperClass="!my-auto desktop:!min-h-fit px-4 desktop:px-6"
      dialogClass="!bg-white dark:!bg-card !border !border-gray-200 dark:!border-card !p-0 !max-h-[calc(100vh-2rem)] !overflow-y-auto rounded-2xl shadow-lg w-full"
      closeClass="!hidden">
      <div className="border-b border-gray-200 dark:border-white/10 px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-xs font-semibold uppercase tracking-[0.15em] text-gray-500 dark:text-white/70">
          Contract Type Change
        </p>
        <h2 className="mt-2 text-lg font-semibold text-gray-900 dark:text-white desktop:text-xl">
          {fromLabel} &rarr; {toLabel}
        </h2>
      </div>

      <div className="flex flex-col gap-4 px-4 py-4 desktop:px-6 desktop:py-5">
        <p className="text-sm leading-relaxed text-gray-600 dark:text-white/75">
          This funding request needs a carrier and carrier invoice amount. Select a carrier and
          enter the invoice amount before changing the contract type.
        </p>

        {hasPreviousMetadata && (
          <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 px-3 py-2.5">
            <p className="text-xs text-blue-700 dark:text-blue-300">
              This funding request was previously factored. The carrier and invoice amount have been
              pre-filled from the previous settings. Please review before confirming.
            </p>
          </div>
        )}

        <div>
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">Carrier</h6>
          <div ref={dropdownRef} className="relative">
            <div className="relative">
              <input
                type="text"
                autoComplete="off"
                placeholder="Search by carrier name"
                value={carrierSearch}
                disabled={isLoadingPreviousCarrier}
                onChange={(e) => {
                  setCarrierSearch(e.target.value);
                  setSelectedCarrier(null);
                  setIsDropdownVisible(true);
                }}
                onFocus={() => {
                  if (!selectedCarrier) setIsDropdownVisible(true);
                }}
                className={`w-full min-h-10 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-card px-3 ${isLoadingPreviousCarrier ? "pr-9" : ""} text-sm text-gray-900 dark:text-white outline-none focus:border-primary ${isLoadingPreviousCarrier ? "opacity-60 cursor-not-allowed" : ""}`}
              />
              {isLoadingPreviousCarrier && (
                <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <svg
                    className="h-4 w-4 animate-spin text-primary"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24">
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    />
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"
                    />
                  </svg>
                </div>
              )}
            </div>
            {isLoadingPreviousCarrier && (
              <p className="mt-1 text-xs text-gray-400 dark:text-white/50">
                Fetching previous carrier...
              </p>
            )}
            {isDropdownVisible && carrierSearch.trim() && !selectedCarrier && (
              <div className="absolute z-30 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-card shadow-lg">
                {isLoadingCarriers ? (
                  <p className="py-3 px-3 text-sm text-gray-500 dark:text-white/60">Loading...</p>
                ) : profileList.length ? (
                  profileList.map((profile) => (
                    <button
                      key={profile.id}
                      type="button"
                      className="w-full px-3 py-2.5 text-left text-sm hover:bg-gray-50 dark:hover:bg-white/5 text-gray-900 dark:text-white"
                      onClick={() => handleCarrierSelect(profile)}>
                      <span className="font-medium">{profile.company_name}</span>
                      {(profile.company_mc || profile.company_dot) && (
                        <span className="ml-2 text-xs text-gray-500 dark:text-white/60">
                          {profile.company_mc
                            ? `MC# ${profile.company_mc}`
                            : `DOT# ${profile.company_dot}`}
                        </span>
                      )}
                    </button>
                  ))
                ) : (
                  <p className="py-3 px-3 text-sm text-gray-500 dark:text-white/60">
                    No carriers found
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <h6 className="mb-1.5 text-sm font-medium text-gray-900 dark:text-white">
            Carrier invoice amount
          </h6>
          <input
            type="number"
            min="0"
            step="0.01"
            placeholder="0.00"
            value={carrierInvoiceAmount}
            onChange={(e) => setCarrierInvoiceAmount(e.target.value)}
            className="w-full min-h-10 rounded-lg border border-gray-200 dark:border-white/10 bg-white dark:bg-card px-3 text-sm text-gray-900 dark:text-white outline-none focus:border-primary"
          />
        </div>
      </div>

      <div className="flex flex-col-reverse gap-2 border-t border-gray-200 dark:border-white/10 px-4 py-4 desktop:flex-row desktop:justify-end desktop:gap-3 desktop:px-6">
        <Button
          type="button"
          rounded
          variant="outline"
          onClick={handleCancel}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[7rem] border-gray-300 dark:border-white/20 bg-transparent text-gray-700 dark:text-white hover:bg-gray-100 dark:hover:bg-white/5">
          Cancel
        </Button>
        <Button
          type="button"
          rounded
          onClick={handleConfirm}
          disabled={isConfirmDisabled}
          className="!h-9 w-full text-sm desktop:w-auto desktop:min-w-[10rem] !bg-primary dark:!bg-white !text-white dark:!text-card hover:!bg-primary/90 dark:hover:!bg-white/90">
          Confirm
        </Button>
      </div>
    </Modal>
  );
}
